import { useEffect, useRef, useState } from 'react';
import { useProfile } from '../hooks/useProfile.js';
import { ME_HANDLE } from '../config.js';
import TickerText from './TickerText.jsx';
import { xrayNotARecord } from '../lib/xray.js';

/**
 * Animated count-up for follower / following counts. Ports the spirit of
 * js/main.js:619-802 — short ramp from 0 to the actual count whenever the
 * value changes.
 */
function useCountUp(target, ms = 700) {
  const [n, setN] = useState(typeof target === 'number' ? target : 0);
  const startRef = useRef(0);
  const fromRef = useRef(0);
  const rafRef = useRef(0);

  useEffect(() => {
    // Nothing to ramp when the count is already showing (a re-mount that
    // starts from the remembered profile).
    if (typeof target !== 'number' || target === n) return;
    cancelAnimationFrame(rafRef.current);
    fromRef.current = n;
    startRef.current = performance.now();
    function tick(t) {
      const elapsed = t - startRef.current;
      const p = Math.min(1, elapsed / ms);
      const eased = 1 - Math.pow(1 - p, 3);
      const next = Math.round(fromRef.current + (target - fromRef.current) * eased);
      setN(next);
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, ms]);

  return n;
}

export default function ProfileStats() {
  const { profile } = useProfile();
  const followers = useCountUp(profile?.followersCount ?? null);
  const hasValue = typeof profile?.followersCount === 'number';
  // Render the row even while the snapshot/live fetch is in flight so the
  // secondary chrome bar doesn't pop in a third line a beat after the other
  // two — matches NowPlaying's placeholder pattern.
  if (!hasValue) {
    return (
      <span className="chrome-signal chrome-signal-stats">
        <span className="chrome-signal-label">followed by</span>
        <span className="chrome-signal-value">&mdash;</span>
      </span>
    );
  }
  const peopleLabel = followers === 1 ? 'person' : 'people';
  // The count leads to where it's kept: Dame's profile on Bluesky, which is
  // where those people follow from.
  return (
    <span className="chrome-signal chrome-signal-stats" {...xrayNotARecord('AppView count')}>
      <span className="chrome-signal-label">followed by</span>
      <TickerText className="chrome-signal-value" title={`followed by ${followers.toLocaleString()} ${peopleLabel} on Bluesky`}>
        <a
          className="chrome-signal-link"
          href={`https://bsky.app/profile/${ME_HANDLE}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <strong>{followers.toLocaleString()}</strong>
          {` ${peopleLabel} on Bluesky`}
        </a>
      </TickerText>
    </span>
  );
}
